import { useEffect, useId, useState } from 'react';
import {
  ACCESS_CHOICES,
  ACCESS_KEYS,
  NO_OVERRIDE,
  PERMISSION_LABEL,
  ROLE_LABEL,
  USER_ACCESS_NOTE_MAX,
  VIEW_LABEL,
  accessChoice,
  accessGaps,
  accessOverrideProblems,
  allowedView,
  can,
  effectivePermissions,
  effectiveViews,
  permissionsFor,
  withAccessChoice,
  type AccessChoice,
  type AccessKey,
  type User,
  type UserAccessOverride,
} from '@/domain';
import { useAccessMatrix, useSaveUserAccess, useUserAccess } from '@/application/usecases/useAdmin';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { formatInstant } from '@/shared/format/instant';
import { Button } from '@/ui/primitives/Button';
import { modalStyles } from '@/ui/primitives/Modal';
import { toast } from '@/ui/primitives/toastStore';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { AccessGaps } from './AccessGaps';
import styles from './SpecialAccessCard.module.css';

const CHOICE_LABEL: Record<AccessChoice, string> = {
  inherit: strings.access.inherit,
  grant: strings.access.grant,
  deny: strings.access.deny,
};

const labelOf = (access: AccessKey) =>
  access.kind === 'permission' ? PERMISSION_LABEL[access.key] : VIEW_LABEL[access.key];

/** "A, B and C", the way the context line reads. */
function listed(labels: readonly string[]): string {
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} ${strings.access.and} ${labels.at(-1) ?? ''}`;
}

/**
 * Special access, on a person's own record. Request of 13 September 2026,
 * section 2.5.
 *
 * You grant access to a person while looking at that person, so this sits on
 * their record rather than as a fourth tab on the Users screen. One row per
 * permission and per screen, generated from the lists the matrix table is
 * generated from, so a permission added later appears here with no edit. Each
 * row is three buttons in the dispatch dialog's choice group, Inherit first and
 * pressed by default, with what the role gives written beneath.
 *
 * Nothing here changes how the application reads access. The server resolves
 * the exceptions into the person's session, on their next sign in or refresh,
 * which is what the card says.
 *
 * Inherit, Grant and Deny only change the draft. Save is the action, and it is
 * asked about before it is sent: the request of 5 October 2026 (third),
 * section 2.
 */
export function SpecialAccessCard({ user, isSelf }: { user: User; isSelf: boolean }) {
  const access = useUserAccess(user.id, true);
  const matrix = useAccessMatrix();
  const save = useSaveUserAccess();
  const ask = useConfirmAction();
  const noteId = useId();

  const [draft, setDraft] = useState<UserAccessOverride>(NO_OVERRIDE);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The server's copy replaces the draft whenever it arrives or changes.
  useEffect(() => {
    if (access.data === undefined) return;
    setDraft({
      grantedPermissions: access.data.grantedPermissions,
      grantedViews: access.data.grantedViews,
      deniedPermissions: access.data.deniedPermissions,
      deniedViews: access.data.deniedViews,
    });
    setNote(access.data.note ?? '');
  }, [access.data]);

  if (access.isPending || matrix.isPending) {
    return (
      <section className={styles.card}>
        <SkeletonRows rows={3} label={strings.access.loading} />
      </section>
    );
  }
  if (access.isError || matrix.isError || access.data === undefined || matrix.data === undefined) {
    return (
      <section className={styles.card}>
        <p className={styles.hint}>{strings.errors.server}</p>
      </section>
    );
  }

  const role = ROLE_LABEL[user.role];
  const held = permissionsFor(user.role, matrix.data).map((key) => PERMISSION_LABEL[key]);
  const roleGives = (row: AccessKey) =>
    row.kind === 'permission'
      ? can(user.role, row.key, matrix.data)
      : allowedView(user.role, row.key, matrix.data);

  /*
   * What this person would hold that will not work as it stands, with the
   * choices on screen applied: their role, their supervisor flag and the
   * exceptions being edited. The lab's request of 11 October 2026.
   */
  const flags = { isSupervisor: user.isSupervisor };
  const gaps = accessGaps(
    user.role,
    effectiveViews(user.role, matrix.data, draft, flags),
    effectivePermissions(user.role, matrix.data, draft, flags),
  ).map((sentence) => ({ sentence }));

  function choose(row: AccessKey, choice: AccessChoice) {
    setDraft((current) => withAccessChoice(current, row, choice));
    setError(null);
  }

  function submit() {
    const problem = Object.values(accessOverrideProblems(draft))[0];
    if (problem !== undefined) {
      setError(problem);
      return;
    }
    ask(
      strings.confirm.userAccess(user.name),
      () =>
        save
          .mutateAsync({
            userId: user.id,
            rowVersion: access.data?.rowVersion ?? '',
            access: { ...draft, note: note.trim() === '' ? null : note.trim() },
          })
          .then(
            () => toast(`${strings.access.saved} ${user.name}`),
            // A 409 or a 422 says what happened, verbatim.
            (failure: Error) => setError(failureWords(failure)),
          ),
      { title: strings.access.title, confirmLabel: strings.access.save },
    );
  }

  const section = (kind: AccessKey['kind'], heading: string) => (
    <div className={styles.group}>
      <h4 className={styles.groupTitle}>{heading}</h4>
      <div className={styles.rows}>
        {ACCESS_KEYS.filter((row) => row.kind === kind).map((row) => {
          const label = labelOf(row);
          const current = accessChoice(draft, row);
          // Every row offers all three choices, whatever the person's role.
          // Grant used to be locked on most screens for somebody in Accounts;
          // the request of 6 October 2026 (fourth) took that away.
          return (
            <div key={`${row.kind}-${row.key}`} className={styles.row}>
              <div>
                <span className={styles.label}>{label}</span>
                {/* What Inherit gives, beneath the name. */}
                <span className={styles.fromRole}>
                  {role} {roleGives(row) ? strings.access.fromRoleYes : strings.access.fromRoleNo}
                </span>
              </div>
              <div className={`${modalStyles.choices} ${styles.compact}`} role="group" aria-label={label}>
                {ACCESS_CHOICES.map((choice) => (
                  <button
                    key={choice}
                    className={current === choice ? modalStyles.on : undefined}
                    aria-pressed={current === choice}
                    disabled={isSelf || save.isPending}
                    onClick={() => choose(row, choice)}
                  >
                    {CHOICE_LABEL[choice]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <section className={styles.card} aria-labelledby={`${noteId}-title`}>
      <h3 id={`${noteId}-title`} className={styles.title}>
        {strings.access.title}
      </h3>
      <p className={styles.hint}>{strings.access.lead}</p>
      <p className={styles.context}>
        {user.name} {strings.access.is} {role}.{' '}
        {held.length === 0
          ? `${role} ${strings.access.roleHoldsNothing}`
          : `${role} ${strings.access.roleHolds} ${listed(held)}.`}
      </p>

      {section('permission', strings.access.permissions)}
      {section('view', strings.access.screens)}

      <AccessGaps gaps={gaps} />

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor={noteId}>
          {strings.access.noteLabel}
        </label>
        <input
          id={noteId}
          className={modalStyles.input}
          value={note}
          maxLength={USER_ACCESS_NOTE_MAX}
          placeholder={strings.access.notePlaceholder}
          disabled={isSelf || save.isPending}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      {error !== null && (
        <p className={modalStyles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.foot}>
        <span className={styles.hint}>
          {isSelf ? strings.access.ownAccess : strings.access.takesEffect}
          {access.data.updatedByUserName !== null && access.data.updatedAt !== null && (
            <>
              {' '}
              {strings.access.lastChanged} {access.data.updatedByUserName} {strings.access.on}{' '}
              {formatInstant(access.data.updatedAt)}.
            </>
          )}
        </span>
        {!isSelf && (
          <Button variant="primary" disabled={save.isPending} onClick={submit}>
            {save.isPending ? strings.access.saving : strings.access.save}
          </Button>
        )}
      </div>
    </section>
  );
}
